import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { baseUrl } from "./baseUrl";
import { saveShopInsightFilter } from "./shopInsightFilterHelpers";

const REASON_LABELS = {
  must_visit: "ویزیت نشده",
  sla_breached: "نقض SLA",
  credit_red: "اعتبار قرمز",
};

export default function VisitorDailyDashboard({ calendarMode = false }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const timer = window.setTimeout(() => {
      axios
        .get(`${baseUrl}/dashboard/today`)
        .then((res) => {
          if (!cancelled) {
            setData(res?.data?.data || null);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setData(null);
          }
        })
        .finally(() => {
          if (!cancelled) {
            setLoading(false);
          }
        });
    // Daily summary is secondary; start it after the shop list request has a head start.
    }, 4000);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, []);

  if (loading || !data) {
    return null;
  }

  const hasStats =
    data.must_visit_remaining > 0 ||
    data.visits_done_today > 0 ||
    data.orders_today > 0 ||
    data.open_crm_leads > 0 ||
    data.crm_sla_breached > 0 ||
    data.credit_at_risk > 0;

  if (!hasStats && (!data.priority_shops || data.priority_shops.length === 0)) {
    return null;
  }

  const goCalendar = () => navigate("/panel/calendar");
  const goShopsWithFilter = (filter) => {
    saveShopInsightFilter(filter);
    navigate(calendarMode ? "/panel/calendar" : "/", {
      state: { applyInsightFilter: true },
    });
  };

  const openShop = (shopId) => {
    navigate(calendarMode ? `/calendar/${shopId}` : `/products/${shopId}`);
  };

  return (
    <div className="visitor-daily-dashboard">
      <div className="visitor-daily-dashboard-head">
        <strong>خلاصه امروز</strong>
        {data.date_label && <span>{data.date_label}</span>}
      </div>

      <div className="visitor-daily-dashboard-grid">
        <button type="button" className="visitor-dash-stat" onClick={goCalendar}>
          <span className="visitor-dash-stat-value">{data.must_visit_remaining}</span>
          <span className="visitor-dash-stat-label">ویزیت باقی‌مانده</span>
        </button>
        <div className="visitor-dash-stat is-static">
          <span className="visitor-dash-stat-value">{data.visits_done_today}</span>
          <span className="visitor-dash-stat-label">ویزیت انجام‌شده</span>
        </div>
        <div className="visitor-dash-stat is-static">
          <span className="visitor-dash-stat-value">{data.orders_today}</span>
          <span className="visitor-dash-stat-label">سفارش امروز</span>
        </div>
        {data.crm_enabled && (
          <>
            <button
              type="button"
              className="visitor-dash-stat"
              onClick={() =>
                goShopsWithFilter({ crm_open: true, crm_sla: false, credit_alert: "" })
              }
            >
              <span className="visitor-dash-stat-value">{data.open_crm_leads}</span>
              <span className="visitor-dash-stat-label">سرنخ CRM باز</span>
            </button>
            <button
              type="button"
              className="visitor-dash-stat is-warning"
              onClick={() =>
                goShopsWithFilter({ crm_open: false, crm_sla: true, credit_alert: "" })
              }
            >
              <span className="visitor-dash-stat-value">{data.crm_sla_breached}</span>
              <span className="visitor-dash-stat-label">نقض SLA</span>
            </button>
          </>
        )}
        <button
          type="button"
          className={`visitor-dash-stat${data.credit_red > 0 ? " is-danger" : data.credit_yellow > 0 ? " is-warning" : ""}`}
          onClick={() =>
            goShopsWithFilter({ crm_open: false, crm_sla: false, credit_alert: "at_risk" })
          }
        >
          <span className="visitor-dash-stat-value">{data.credit_at_risk}</span>
          <span className="visitor-dash-stat-label">اعتبار در خطر</span>
        </button>
      </div>

      {data.priority_shops?.length > 0 && (
        <div className="visitor-daily-priority">
          <div className="visitor-daily-priority-title">اولویت امروز</div>
          {data.priority_shops.map((shop) => (
            <button
              key={shop.id}
              type="button"
              className="visitor-daily-priority-item"
              onClick={() => openShop(shop.id)}
            >
              <span className="visitor-daily-priority-name">{shop.store_name}</span>
              <span className="visitor-daily-priority-tags">
                {(shop.reasons || []).map((reason) => (
                  <span key={reason} className={`visitor-dash-tag tag-${reason}`}>
                    {REASON_LABELS[reason] || reason}
                  </span>
                ))}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
